import { api } from 'src/common/utils/api';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { Logger } from 'src/utils/Logger';
import { StoredMessage } from '../chat/types';
import { SiestaMode } from '../types';

type OpenConversationDeps = {
  currentConversationId: string;
  flagEnabledSiestaChats: boolean;
  loadHistory: (messages: StoredMessage[]) => void;
  beginHistoryLoad: () => void;
  endHistoryLoad: () => void;
  resumeActiveTurn: (conversationId: string, turnId: string) => void;
  appendFailedMarker: () => void;
  setConversationId: (id: string) => void;
  setSiestaMode: (mode: SiestaMode) => void;
};

export function openConversationFromServer(id: string, deps: OpenConversationDeps): void {
  const {
    currentConversationId,
    flagEnabledSiestaChats,
    loadHistory,
    beginHistoryLoad,
    endHistoryLoad,
    resumeActiveTurn,
    appendFailedMarker,
    setConversationId,
    setSiestaMode,
  } = deps;

  const switching = id !== currentConversationId;
  if (switching) beginHistoryLoad();
  setSiestaMode('chat');
  void (async () => {
    try {
      const { data } = await api.get<{
        messages: StoredMessage[];
        activeTurnId: string | null;
        lastTurnFailed: boolean;
      }>(`conversations/${id}`);
      setConversationId(id);
      loadHistory(data.messages);
      // Opening the chat shows its latest answer, so clear the unread badge. Not gated on async
      // generation: with stream persistence on, the live path completes a turn too, and nothing
      // else marks it read.
      if (flagEnabledSiestaChats) {
        if (data.activeTurnId) {
          // Still generating: the resumed stream marks it read when it finishes.
          resumeActiveTurn(id, data.activeTurnId);
        } else if (data.lastTurnFailed) {
          // The worker never returned and the sweep finalised the turn: show the give-up state
          // instead of a question that looks silently unanswered.
          appendFailedMarker();
        } else {
          api
            .put(`conversations/${id}/read`)
            .catch(error => Logger.error('Could not mark chat as read', error));
        }
      }
    } catch (error) {
      endHistoryLoad();
      if (switching) setSiestaMode('history');
      notifyAndLogError(
        'Could not open chat',
        'The chat could not be opened. Please try again.',
        error,
      );
    }
  })();
}
